<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { useChatStore, useChatStoreLanguage } from '../stores/chatStore';
import DeepResearchPanel from './DeepResearchPanel.vue';
import FollowUpStatus from './FollowUpStatus.vue';
import FollowUpHistory from './FollowUpHistory.vue';

const { setLocale, locale, t } = useI18n()
const chatStore = useChatStore();
const chatStoreLanguage = useChatStoreLanguage();
const config = useRuntimeConfig();
const { isMobile } = useDevice();
let isClientMobile = ref(false);
const chatModeScrollRef = ref<HTMLElement | null>(null);
const isFullScreenLayout = computed(() =>
  chatStore.windowExtend ||
  isClientMobile.value ||
  chatStore.isNewWindow ||
  chatStore.isIframe
);
const route = useRoute();

// マウスホイールの縦スクロールを横スクロールに変換（PC向け）
const handleHorizontalWheel = (event: WheelEvent) => {
  if (!chatModeScrollRef.value) return;

  const { deltaY, deltaX } = event;
  if (Math.abs(deltaY) > Math.abs(deltaX)) {
    chatModeScrollRef.value.scrollLeft += deltaY;
    event.preventDefault();
  }
};

/**
 * 初期処理
 */
chatStoreLanguage.setInit();
await chatStore.initChat();

/**
 * 起動モーダル
 */
const closeAttentionModal = () => {
  chatStore.acknowledgeAttentionModal();
};

onMounted(async () => {
  // 新規ウィンドウ表示の判定
  if (route.query.isNewWindow) {
    chatStore.isNewWindow = true;
    chatStore.windowClosed = false;
  }

  isClientMobile.value = isMobile;

  //ブラウザの言語コード取得
  let language = navigator.language;
  if (!language.startsWith("zh")) {
    //中国以外
    //国コードを省いた言語コードのみ取得
    language = language.split("-")[0];
  }

  console.log(`browser language=${language}`);
  chatStore.language = config.public.supportedLanguages.includes(language) ? language : config.public.otherJapaneseDefaultLanguage;
  setLocale(chatStore.language.replace(/-/g, ''));
  console.log(`use language=${chatStore.language}`);

  //表示初期処理
  await chatStore.initDsp();
});
</script>

<template>
  <div class="aichat-window overflow-hidden" v-show="!chatStore.windowClosed || chatStore.isNewWindow" :class="isFullScreenLayout
    ? 'fixed inset-0 z-[10000] !w-screen h-dvh max-h-dvh max-w-full !static'
    : 'sm:max-h-[calc(100vh-1.5rem)] sm:h-[600px] sm:bottom-4 sm:w-[450px]'">
    <div class="h-full flex flex-col border-l border-b border-r aichat-bg border-gray-300 relative min-h-0"
      :class="chatStore.createLanguageClass('ja')">
      <Header />
      <div v-show="!chatStore.attentionModal" v-if="chatStore.modes.length > 1"
        class="flex items-center gap-2 px-2 overflow-x-auto whitespace-nowrap h-[54px] bg-[#F7F7F7] border-b border-white">
        <template v-for="mode in chatStore.modes">
          <button type="button" class="chat-mode-btn" :class="{ 'selected': chatStore.currentMode == mode.code }"
            @click="chatStore.setMode(mode.code)">
            {{ mode.name }}
          </button>
        </template>
      </div>
      <div v-if="false && !chatStore.attentionModal"
        class="flex items-center gap-2 px-2 py-2 bg-white border-b border-gray-200">
        <button type="button" class="chat-panel-tab" :class="{ 'selected': chatStore.activePanel === 'chat' }"
          @click="chatStore.setActivePanel('chat')">
          {{ $t('chatTabChat') }}
        </button>
        <button type="button" class="chat-panel-tab" :class="{ 'selected': chatStore.activePanel === 'history' }"
          @click="chatStore.setActivePanel('history')">
          <span class="flex items-center gap-1">
            {{ $t('chatTabHistory') }}
            <span v-if="chatStore.followUpHistory.length" class="text-[10px] text-gray-500">
              {{ chatStore.followUpHistory.length }}
            </span>
          </span>
        </button>
      </div>
      <div class="flex-1 overflow-y-hidden min-h-0">
        <div v-if="!chatStore.attentionModal" class="h-full">
          <div v-if="chatStore.activePanel === 'chat'" class="flex flex-col h-full min-h-0">
            <div v-if="chatStore.followUpQueue.length || chatStore.isProcessingFollowUps"
              class="px-3 pt-3 pb-2 bg-[#F7F7F7] border-b border-gray-200">
              <FollowUpStatus />
            </div>
            <ChatMessageContainer />
            <InputField />
          </div>
          <div v-else class="h-full">
            <FollowUpHistory />
          </div>
        </div>
        <Attention :isOpen="chatStore.attentionModal" @closeModalEvent="closeAttentionModal" />
      </div>
      <ReviewModal :isOpen="chatStore.reviewModal" :isClose="chatStore.reviewModal" />
      <DeepResearchPanel />
    </div>
  </div>
</template>

<style>
@reference "../assets/css/tailwind.css";

::-webkit-scrollbar {
  /* スクロールバー全体の幅 */
  width: 13px;

  /* スクロールバーの背景色 */
  background-color: none
}

::-webkit-scrollbar-thumb {
  /* スクロールバーの丸み */
  border-radius: 10px;

  /* スクロールバーの色 */
  box-shadow: inset 0 0 10px 10px #C3C3C3;

  /* スクロールバーの左右の余白 */
  border: solid 4px transparent;
}

.chat-mode-btn {
  @apply text-[0.82em];
  @apply bg-white;
  @apply rounded-md;
  @apply border-solid;
  @apply border;
  @apply border-[#707070];
  @apply p-1.5;
  @apply hover:bg-default-btn-hover;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);
}

.chat-mode-btn.selected {
  @apply bg-[#000000cc];
  @apply text-white;
}

.chat-panel-tab {
  @apply text-xs;
  @apply bg-white;
  @apply rounded-md;
  @apply border;
  @apply border-[#D9D9D9];
  @apply px-3;
  @apply py-1.5;
  @apply text-gray-600;
  @apply hover:bg-default-btn-hover;
  @apply transition-colors;
}

.chat-panel-tab.selected {
  @apply bg-[#000000cc];
  @apply text-white;
  @apply border-[#000000cc];
}

/* hide horizontal scrollbar for chat-mode row while keeping scrollability */
.no-scrollbar {
  scrollbar-width: none;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
